import { useCallback, useEffect } from 'react'
import {
	isSearching,
	idSearch,
	realTimeFilters,
	realTimeTasks,
	userComesFromUrl,
	tableColorsSuccessRange,
	tableColorsMediumRange,
	tableColorsErrorRange
} from 'Pages/RealTimeHistory/RealTime/@signals'
import { getSubmit, HeatColor } from '@ui-seeker/utils'
import { BACKEND_APIS } from '@constants'
import { batch, useSignal, useComputed } from '@preact/signals-react'
import { setCommonFilters } from '@signals'
import { useToast } from '@hooks'
import { useTheme } from '@mui/material'


export default function useRealTimeStatus() {
	const { showToast } = useToast()
	const tableData = useSignal([])
	const timeStampCreate = useSignal(null)
	const tableDataAccessDirectSite = useSignal(false)
	const tableDataAccessBrokers = useSignal(false)
	const tableDataErrors = useSignal({})
	const tableDataHasResults = useSignal(false)

	const { palette } = useTheme()
	const firstThirdIndex = useComputed(() => Math.floor(tableData.value.length / 3))
	const secondThirdIndex = useComputed(() => Math.floor(firstThirdIndex.value * 2))


	const generateTableColor = useCallback(() => batch(() => {
		const diff = 10

		tableColorsSuccessRange.value = new HeatColor({
			min: tableData.value.length ? tableData.value[0].price - diff : 0,
			max: tableData.value.length ? tableData.value[firstThirdIndex.value].price + diff : 0,
			color: palette.success.main,
			textColorWhite: palette.text.primary,
			textColorDark: palette.text.primary,
			inverse: true,
			maxLightness: 0.7,
		})

		tableColorsMediumRange.value = new HeatColor({
			min: tableData.value.length ? tableData.value[firstThirdIndex.value].price - diff : 0,
			max: tableData.value.length ? tableData.value[secondThirdIndex.value].price + diff : 0,
			color: palette.warning.main,
			textColorWhite: palette.text.primary,
			textColorDark: palette.text.primary,
			maxLightness: 0.7,
		})

		tableColorsErrorRange.value = new HeatColor({
			min: tableData.value.length ? tableData.value[secondThirdIndex.value].price - diff : 0,
			max: tableData.value.length ? tableData.value[tableData.value.length - 1].price + diff : 0,
			color: palette.error.main,
			textColorWhite: palette.text.primary,
			textColorDark: palette.text.primary,
			maxLightness: 0.7,
		})
	}), [tableData.value, idSearch.value, firstThirdIndex.value, secondThirdIndex.value])

	const _getPartialResults = useCallback((arrayApis, allFinished) => {
		arrayApis.forEach(url => {
			getSubmit(url).then(res => {
				if (realTimeTasks.value?.numTasks > 0 && realTimeTasks.value?.numTasks <= realTimeTasks.value?.completeTasks) {
					tableData.value = res.results
				}
			}).finally(() => {
				if (allFinished) isSearching.value = false
			})
		})
	}, [])


	const getStatus = useCallback(() => {
		if (!idSearch.value) return
		getSubmit(`${BACKEND_APIS.SEARCH}/manual/status?id_search=${idSearch.value}&isFromUrl=${userComesFromUrl.value}`)
			.then(result => batch(() => {
				const {
					filters, allFinished, completeTasks, totalTasks, hasCompetitionSites,
					hasDirectSites, errorTasksV2, hasResults, resultApi
				} = result
				setCommonFilters(filters)

				if (resultApi.length) {
					setTimeout(() => {
						_getPartialResults(resultApi, allFinished)
					}, 1000)
				} else if(allFinished) {
					isSearching.value = false
				}

				timeStampCreate.value = filters?.timestamp_create_user
				realTimeTasks.value = { numTasks: completeTasks, completeTasks: totalTasks }
				tableDataAccessDirectSite.value = hasDirectSites
				tableDataAccessBrokers.value = hasCompetitionSites
				tableDataErrors.value = errorTasksV2
				tableDataHasResults.value = hasResults

				if (!allFinished && isSearching.value) {
					setTimeout(() => {
						getStatus()
					}, 5000)
				} else {
					showToast.info('Showing data, please wait')
				}
		})).catch(() => {
			showToast.error()
		})
	}, [idSearch.value, JSON.stringify(realTimeFilters.value)])

	useEffect(() => {
		if(idSearch.value) {
			tableData.value = []
			getStatus()
		} else {
			tableData.value = []
			timeStampCreate.value = null
			tableDataAccessDirectSite.value = false
			tableDataAccessBrokers.value = false
			tableDataErrors.value = {}
			tableDataHasResults.value = false
		}
	}, [idSearch.value])

	return {
		tableData: tableData.value,
		timeStampCreate: timeStampCreate.value,
		tableDataAccessDirectSite: tableDataAccessDirectSite.value,
		tableDataAccessBrokers: tableDataAccessBrokers.value,
		tableDataErrors: tableDataErrors.value,
		tableDataHasResults: tableDataHasResults.value,
		firstThirdIndex: firstThirdIndex.value,
		secondThirdIndex: secondThirdIndex.value,
		generateTableColor
	}
}